import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import {
  ACCOUNTS_READ_ROLES,
  ADMISSIONS_DELETE_ROLES,
  ADMISSIONS_WRITE_ROLES,
  EXAM_BOOKINGS_READ_ROLES,
  STUDENT_AFFAIRS_READ_ROLES,
  STUDENT_BRANCH_TRANSFER_ROLES,
  hasRole,
} from '@/auth/roles';
import { deleteStudent, getStudent, restoreStudent } from '@/api/students.api';
import { ApiError } from '@/api/errors';
import { Banner } from '@/components/common/Banner';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { StatusChip } from '@/components/common/StatusChip';
import { TransferBranchPanel } from '@/pages/students/TransferBranchPanel';
import { showSuccessToast } from '@/lib/toast';
import { formatCurrencyEGP, formatDate, formatDateTime } from '@/lib/format';
import { lookupLabel } from '@/lib/lookups';
import { toneForStudentStatus } from '@/lib/studentStatus';
import type { FieldWarning } from '@/api/students.api';
import type { StudentResponse } from '@/types/shared';
import './StudentDetailPage.css';

interface LocationState {
  successMessage?: string;
  warnings?: FieldWarning[];
}

export function StudentDetailPage() {
  const { t, i18n } = useTranslation();
  const { studentCode } = useParams<{ studentCode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const canEdit = hasRole(user?.role, ADMISSIONS_WRITE_ROLES);
  const canDelete = hasRole(user?.role, ADMISSIONS_DELETE_ROLES);
  const canTransferBranch = hasRole(user?.role, STUDENT_BRANCH_TRANSFER_ROLES);
  const canViewStudentAffairs = hasRole(user?.role, STUDENT_AFFAIRS_READ_ROLES);
  const canViewAccounts = hasRole(user?.role, ACCOUNTS_READ_ROLES);
  const canViewExamBookings = hasRole(user?.role, EXAM_BOOKINGS_READ_ROLES);

  const [student, setStudent] = useState<StudentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);

  const isDeleted = Boolean(student?.deletedAt);

  const locationState = (location.state as LocationState | null) ?? null;

  useEffect(() => {
    if (!studentCode) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getStudent(studentCode)
      .then((data) => {
        if (!cancelled) setStudent(data);
      })
      .catch((err) => {
        if (cancelled) return;
        const apiError = ApiError.fromUnknown(err);
        setError(
          apiError.code === 'NOT_FOUND'
            ? t('students.detail.notFound')
            : t('students.detail.loadError'),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studentCode, t]);

  async function handleConfirmDelete() {
    if (!studentCode) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteStudent(studentCode);
      setConfirmDeleteOpen(false);
      const message = t('students.detail.deleteSuccess', { code: studentCode });
      showSuccessToast(message);
      navigate('/students', {
        state: { successMessage: message },
      });
    } catch {
      setDeleteError(t('students.detail.deleteError'));
    } finally {
      setDeleting(false);
    }
  }

  async function handleRestore() {
    if (!studentCode) return;
    setRestoring(true);
    setDeleteError(null);
    try {
      const restored = await restoreStudent(studentCode);
      setStudent(restored);
    } catch {
      setDeleteError(t('students.restore.genericError'));
    } finally {
      setRestoring(false);
    }
  }

  if (loading) {
    return <p className="fa-form-loading">{t('common.loading')}</p>;
  }

  if (error || !student) {
    return (
      <div>
        <Banner tone="danger" assertive>
          {error ?? t('students.detail.notFound')}
        </Banner>
        <Link to="/students" className="fa-btn fa-btn--secondary">
          {t('students.detail.backToList')}
        </Link>
      </div>
    );
  }

  return (
    <section className="fa-student-detail">
      {locationState?.successMessage && (
        <Banner tone="success">{locationState.successMessage}</Banner>
      )}
      {locationState?.warnings && locationState.warnings.length > 0 && (
        <Banner tone="warning">
          <strong>{t('students.detail.warningsTitle')}</strong>
          <ul>
            {locationState.warnings.map((w, idx) => (
              <li key={idx}>{w.message}</li>
            ))}
          </ul>
        </Banner>
      )}
      {deleteError && (
        <Banner tone="danger" assertive>
          {deleteError}
        </Banner>
      )}
      {isDeleted && <Banner tone="warning">{t('students.detail.deletedBanner')}</Banner>}

      <header className="fa-student-detail__header">
        <div className="fa-student-detail__heading">
          <span className="fa-student-detail__avatar" aria-hidden="true">
            {student.nameEn.trim().charAt(0).toUpperCase() || student.nameAr.trim().charAt(0)}
          </span>
          <div>
            <h1 className="fa-student-detail__title">
              <span className="fa-ltr-isolate">{student.studentCode}</span>
              {' — '}
              {student.nameAr}
            </h1>
            <p className="fa-student-detail__subtitle">{student.nameEn}</p>
            {student.linkedRecords.studentStatus && (
              <StatusChip
                label={t(`enums.studentStatus.${student.linkedRecords.studentStatus}`)}
                tone={toneForStudentStatus(student.linkedRecords.studentStatus)}
              />
            )}
          </div>
        </div>
        <div className="fa-student-detail__actions">
          {isDeleted ? (
            canDelete && (
              <button
                type="button"
                className="fa-btn fa-btn--primary"
                onClick={handleRestore}
                disabled={restoring}
              >
                {t('students.detail.restoreButton')}
              </button>
            )
          ) : (
            <>
              {canEdit && (
                <button
                  type="button"
                  className="fa-btn fa-btn--secondary"
                  onClick={() => navigate(`/students/${student.studentCode}/edit`)}
                >
                  {t('students.detail.editButton')}
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="fa-btn fa-btn--destructive"
                  onClick={() => setConfirmDeleteOpen(true)}
                >
                  {t('students.detail.deleteButton')}
                </button>
              )}
            </>
          )}
        </div>
      </header>

      <div className="fa-student-detail__grid">
        <section className="fa-form-section">
          <h2 className="fa-form-section__title">{t('students.detail.studentInfoTitle')}</h2>
          <dl className="fa-detail-list">
            <DetailRow
              label={t('students.detail.branch')}
              value={lookupLabel(student.branch, i18n.language)}
            />
            <DetailRow
              label={t('students.detail.dateOfBirth')}
              value={formatDate(student.dateOfBirth, i18n.language)}
            />
            <DetailRow
              label={t('students.detail.gender')}
              value={t(`enums.gender.${student.gender}`)}
            />
            <DetailRow
              label={t('students.detail.grade')}
              value={lookupLabel(student.grade, i18n.language)}
            />
            <DetailRow
              label={t('students.detail.school')}
              value={lookupLabel(student.agreedSchool, i18n.language)}
            />
            <DetailRow
              label={t('students.detail.educationalAdministration')}
              value={lookupLabel(student.educationalAdministration, i18n.language)}
            />
            <DetailRow
              label={t('students.detail.nationalId')}
              value={student.nationalId ?? '—'}
              ltr
            />
            <DetailRow label={t('students.detail.address')} value={student.address ?? '—'} />
            <DetailRow
              label={t('students.detail.previousSchool')}
              value={student.previousSchool ?? '—'}
            />
            <DetailRow
              label={t('students.detail.applicationSource')}
              value={t(`enums.applicationSource.${student.applicationSource}`)}
            />
            <DetailRow
              label={t('students.detail.applicationDate')}
              value={formatDate(student.applicationDate, i18n.language)}
            />
            <DetailRow
              label={t('students.detail.admissionNotes')}
              value={student.admissionNotes ?? '—'}
            />
            {/* T152 (`docs/architecture/16-referral-partners.md` §7 endpoint 10, §10) — read-only,
                name only, never a money figure (that lives on the Accounts referral card, T153).
                Visible to every existing student-read role since `referralPartner` rides on
                `StudentResponse`, not a finance-gated endpoint. */}
            <DetailRow
              label={t('students.detail.referralPartner')}
              value={
                student.referralPartner
                  ? lookupLabel(student.referralPartner, i18n.language)
                  : t('students.detail.notReferred')
              }
            />
          </dl>

          {/* T175 (§20, DEC-BR17/FR-20) — Admin-only, and never on a soft-deleted student (there's
              nothing "ongoing" to transfer). Not a field on the edit form above — `branchId` isn't
              part of `PATCH /students/{studentCode}` at all (DEC-BRA6); this dedicated action is
              the only way to change it. */}
          {canTransferBranch && !isDeleted && (
            <div className="fa-student-detail__transfer">
              <TransferBranchPanel student={student} onTransferred={setStudent} />
            </div>
          )}
        </section>

        <section className="fa-form-section">
          <h2 className="fa-form-section__title">{t('students.detail.guardianInfoTitle')}</h2>
          <dl className="fa-detail-list">
            <DetailRow
              label={t('students.detail.guardianName')}
              value={student.guardianNameAr || student.guardianNameEn || '—'}
            />
            <DetailRow
              label={t('students.detail.guardianRelationship')}
              value={t(`enums.guardianRelationship.${student.guardianRelationship}`)}
            />
            <DetailRow
              label={t('students.detail.guardianPhone')}
              value={student.guardianPhone}
              ltr
            />
            <DetailRow
              label={t('students.detail.guardianSecondaryPhone')}
              value={student.guardianSecondaryPhone ?? '—'}
              ltr
            />
            <DetailRow
              label={t('students.detail.guardianEmail')}
              value={student.guardianEmail ?? '—'}
              ltr
            />
            <DetailRow
              label={t('students.detail.guardianNationalId')}
              value={student.guardianNationalId ?? '—'}
              ltr
            />
          </dl>
        </section>

        <section className="fa-form-section">
          <div className="fa-student-detail__section-header">
            <h2 className="fa-form-section__title">{t('students.detail.linkedRecordsTitle')}</h2>
            <div className="fa-student-detail__section-header-links">
              {canViewStudentAffairs && (
                <Link
                  to={`/student-affairs/${student.studentCode}`}
                  className="fa-btn fa-btn--secondary fa-student-detail__affairs-link"
                >
                  {t('students.detail.viewStudentAffairs')}
                </Link>
              )}
              {canViewAccounts && (
                <Link
                  to={`/accounts/${student.studentCode}`}
                  className="fa-btn fa-btn--secondary fa-student-detail__affairs-link"
                >
                  {t('students.detail.viewAccounts')}
                </Link>
              )}
              {canViewExamBookings && (
                <Link
                  to={`/exam-bookings/${student.studentCode}`}
                  className="fa-btn fa-btn--secondary fa-student-detail__affairs-link"
                >
                  {t('students.detail.viewExamBookings')}
                </Link>
              )}
            </div>
          </div>
          {/* Linked-records summary cards — each stat gets its own tile instead of a plain
              label:value list, for a quicker at-a-glance scan. `<div>` wrappers around each
              dt/dd pair keep this a valid `<dl>` name-value list for assistive tech. */}
          <dl className="fa-stat-grid">
            <div className="fa-stat-card">
              <dt className="fa-stat-card__label">{t('students.detail.studentStatus')}</dt>
              <dd className="fa-stat-card__value">
                {student.linkedRecords.studentStatus ? (
                  <StatusChip
                    label={t(`enums.studentStatus.${student.linkedRecords.studentStatus}`)}
                    tone={toneForStudentStatus(student.linkedRecords.studentStatus)}
                  />
                ) : (
                  <span className="fa-stat-card__muted">{t('students.detail.notSet')}</span>
                )}
              </dd>
            </div>
            <div className="fa-stat-card fa-stat-card--wide">
              <dt className="fa-stat-card__label">{t('students.detail.documents')}</dt>
              <dd className="fa-stat-card__value">
                <div
                  role="progressbar"
                  aria-valuenow={student.linkedRecords.documentsReceived}
                  aria-valuemin={0}
                  aria-valuemax={student.linkedRecords.documentsTotal}
                  className="fa-progress"
                >
                  <div
                    className="fa-progress__bar"
                    style={{
                      width: `${
                        student.linkedRecords.documentsTotal > 0
                          ? (student.linkedRecords.documentsReceived /
                              student.linkedRecords.documentsTotal) *
                            100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <bdi>
                  {t('students.detail.documentsValue', {
                    received: student.linkedRecords.documentsReceived,
                    total: student.linkedRecords.documentsTotal,
                  })}
                </bdi>
              </dd>
            </div>
            <div className="fa-stat-card">
              <dt className="fa-stat-card__label">{t('students.detail.totalPaid')}</dt>
              <dd className="fa-stat-card__value fa-ltr-isolate">
                {formatCurrencyEGP(student.linkedRecords.totalPaid, i18n.language)}
              </dd>
            </div>
            <div className="fa-stat-card">
              <dt className="fa-stat-card__label">{t('students.detail.totalRemaining')}</dt>
              <dd className="fa-stat-card__value fa-ltr-isolate">
                {formatCurrencyEGP(student.linkedRecords.totalRemaining, i18n.language)}
              </dd>
            </div>
            <div className="fa-stat-card">
              <dt className="fa-stat-card__label">{t('students.detail.examsBooked')}</dt>
              <dd className="fa-stat-card__value">{student.linkedRecords.examsBooked}</dd>
            </div>
          </dl>
        </section>
      </div>

      <section className="fa-form-section fa-student-detail__audit">
        <h2 className="fa-form-section__title">{t('students.detail.auditTitle')}</h2>
        <div className="fa-student-detail__audit-grid">
          <p>
            <span className="fa-student-detail__audit-label">{t('students.detail.addedBy')}</span>
            <span>
              {student.createdBy?.fullName ?? t('students.detail.unknownUser')}
              {' — '}
              <bdi>{formatDateTime(student.createdAt, i18n.language)}</bdi>
            </span>
          </p>
          <p>
            <span className="fa-student-detail__audit-label">{t('students.detail.updatedBy')}</span>
            <span>
              {student.updatedBy?.fullName ?? t('students.detail.unknownUser')}
              {' — '}
              <bdi>{formatDateTime(student.updatedAt, i18n.language)}</bdi>
            </span>
          </p>
        </div>
      </section>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title={t('students.detail.deleteConfirmTitle')}
        description={t('students.detail.deleteConfirmBody', { code: student.studentCode })}
        confirmLabel={t('students.detail.deleteConfirmAction')}
        cancelLabel={t('students.form.cancel')}
        destructive
        confirmDisabled={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </section>
  );
}

function DetailRow({ label, value, ltr }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div className="fa-detail-row">
      <dt>{label}</dt>
      <dd className={ltr ? 'fa-ltr-isolate' : undefined}>{value}</dd>
    </div>
  );
}
